import cx from "classnames"
import { graphql, Link } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import Button from "components/Button"
import Img from "components/Img"
import { richTextToHtml } from "utils/richTextToHtml"
import * as css from "./Column2BulletGrid.module.css"
import Section from "components/Layout/Section"
import SectionHeader from "components/Layout/SectionHeader"
import SimpleHeader from "components/Layout/SimpleHeader"

const VERTICAL_LAYOUT_OPTION = "Vertical"
const HORIZONTAL_LAYOUT_OPTION = "Horizontal"

function Column2BulletGrid({
  label,
  heading,
  description,
  bulletItemsLayout,
  bulletItems,
  bottomCtaDescription,
  bottomCta,
  image,
  compact,
  inverted,
  background,
}) {
  const isVerticalLayout = () =>
    !bulletItemsLayout || bulletItemsLayout === VERTICAL_LAYOUT_OPTION

  const isHorizontalLayout = () =>
    bulletItemsLayout === HORIZONTAL_LAYOUT_OPTION

  return (
    <Section
      className={css.root}
      padding="extraLight"
      inverted={inverted}
      background={background}
    >
      <SimpleHeader
        label={label}
        title={heading}
        subtitle={description}
        dash="vertical"
        narrow="both"
      />
      {bulletItems?.length > 0 && (
        <div
          className={cx("grid grid-cols-1 md:grid-cols-2 gap-6", css.grid, {
            [css.hasImage]: !!image,
            [css.hasSpace]: isHorizontalLayout(),
            [css.compactGrid]: compact,
          })}
        >
          {bulletItems.map((item) => {
            const richTextDoc = item.richText?.raw
              ? JSON.parse(item.richText?.raw)
              : null
            return (
              <div key={item.id}>
                <div
                  className={cx(css.item, {
                    [css.verticalLayout]: isVerticalLayout(),
                    [css.horizontalLayout]: isHorizontalLayout(),
                    [css.compact]: compact,
                  })}
                >
                  {item.image && (
                    <div className={css.image}>
                      <img src={item.image.url} alt={item.image.title} />
                    </div>
                  )}
                  <div className={css.content}>
                    {item.heading && (
                      <h3 className={css.heading}>
                        {item.link ? (
                          <Link to={item.link} className={css.headingLink}>
                            {item.heading}
                          </Link>
                        ) : (
                          item.heading
                        )}
                      </h3>
                    )}
                    {item.description && (
                      <p className={css.description}>{item.description}</p>
                    )}
                    {richTextDoc && (
                      <div
                        className={css.description}
                        dangerouslySetInnerHTML={{
                          __html: richTextToHtml(richTextDoc).replace(
                            /\n/g,
                            `<br />`
                          ),
                        }}
                      />
                    )}
                    {item.link && (
                      <Link to={item.link} className={css.learnMore}>
                        Learn more →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {(bottomCtaDescription || bottomCta) && (
        <SectionHeader
          subtitle={bottomCtaDescription}
          cta={
            bottomCta && (
              <Button
                rounded={bottomCta.roundedCorner}
                color={bottomCta.color}
                large
                href={bottomCta.url}
                trackClick={bottomCta.trackingName}
              >
                {bottomCta.buttonLabel}
              </Button>
            )
          }
          contrasted
        />
      )}
      {image && (
        <div className={css.backgroundContainer}>
          <Img
            image={image.gatsbyImageData ? image : undefined}
            src={image.url}
            className={css.backgroundImage}
            alt={image.title}
          />
        </div>
      )}
    </Section>
  )
}

Column2BulletGrid.propTypes = {
  label: PropTypes.string,
  heading: PropTypes.string,
  description: PropTypes.string,
  bulletItemsLayout: PropTypes.oneOf([
    VERTICAL_LAYOUT_OPTION,
    HORIZONTAL_LAYOUT_OPTION,
  ]),
  bulletItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      heading: PropTypes.string,
      description: PropTypes.string,
      richText: PropTypes.shape({
        raw: PropTypes.string,
      }),
      image: PropTypes.shape({}),
    })
  ),
  bottomCtaDescription: PropTypes.string,
  bottomCta: PropTypes.shape({
    buttonLabel: PropTypes.string,
    color: PropTypes.string,
    roundedCorner: PropTypes.bool,
    url: PropTypes.string,
    trackingName: PropTypes.string,
  }),
  image: PropTypes.shape({}),
  compact: PropTypes.bool,
  inverted: PropTypes.bool,
  background: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

export default Column2BulletGrid

export const query = graphql`
  fragment Column2BulletGrid on ContentfulColumn2BulletGrid {
    id
    __typename
    label
    heading
    description
    bottomCtaDescription
    bulletItemsLayout
    bulletItems {
      id
      heading
      description
      richText {
        raw
      }
      image {
        url
      }
    }
    bottomCta {
      buttonLabel
      color
      roundedCorner
      url
      trackingName
    }
    image {
      gatsbyImageData(quality: 100)
      title
    }
  }
`
